<template>
  <div class="page-content flex flex-col">
    <div class="title">
      Category Manager
    </div>
    <div
      v-if="
        imagesChecked && missingTotal.categories + missingTotal.subcategories
      "
      class="missing-images-alert warning"
    >
      <span>
        Missing images:
        <b>{{ missingTotal.categories }}</b>
        {{ missingTotal.categories == 1 ? "category" : "categories" }} and
        <b>{{ missingTotal.subcategories }}</b>
        {{ missingTotal.subcategories == 1 ? "subcategory" : "subcategories" }}
        (active only).
      </span>
      <label class="only-missing">
        <input type="checkbox" v-model="onlyMissing" />
        Show only categories with missing images
      </label>
    </div>
    <div v-else-if="imagesChecked" class="missing-images-alert ok">
      All active categories and subcategories have an image.
    </div>
    <div class="list">
      <EditCategoryModal
        v-if="editCategory"
        :category="editCategory"
        :initialSection="editSection"
        @closedCategoryEdition="closedCategoryEdition"
        class="category-edit-modal"
        @updatedCategory="updatedCategory"
        @createdCategory="createdCategory"
      />
      <div
        class="card"
        v-for="category in categoriesShown"
        :key="category.id"
        :class="{
          'has-missing': missingImages(category).total,
          inactive: category.status != '1'
        }"
      >
        <div class="cover" @click="openCategory(category)">
          <img
            v-if="showImage(category.image)"
            :src="category.image"
            :alt="category.categoryName"
            loading="lazy"
            @error="markBroken(category.image)"
          />
          <div v-else class="placeholder">
            <img src="@/assets/icons/upload-clear.svg" alt="" />
            <span>
              {{ hasImage(category.image) ? "Image not found" : "No image" }}
            </span>
          </div>
          <span
            class="status-pill"
            :class="category.status == '1' ? 'active' : 'disabled'"
          >
            {{ category.status == "1" ? "Active" : "Inactive" }}
          </span>
          <div class="options" @click.stop>
            <a @click="openCategory(category)" class="icon" title="Edit">
              <img src="@/assets/icons/edit.svg" />
            </a>
            <a class="icon" @click="removeCategory(category)" title="Delete">
              <img src="@/assets/icons/trash.svg" />
            </a>
          </div>
        </div>
        <div class="body">
          <div class="name">{{ category.categoryName }}</div>
          <div class="meta">
            <template v-if="subcategoriesByCategory[category.id]">
              {{ subcategoriesByCategory[category.id].length }}
              {{
                subcategoriesByCategory[category.id].length == 1
                  ? "subcategory"
                  : "subcategories"
              }}
            </template>
            <template v-else>Loading subcategories…</template>
          </div>
          <div
            class="thumbs"
            v-if="(subcategoriesByCategory[category.id] || []).length"
            @click="openCategory(category, 'subcategories')"
          >
            <span
              v-for="sub in thumbsOf(category).shown"
              :key="sub.id"
              class="thumb"
              :class="{ missing: !showImage(sub.subCategoryImage) }"
              :title="sub.subCategoryName"
            >
              <img
                v-if="showImage(sub.subCategoryImage)"
                :src="sub.subCategoryImage"
                alt=""
                loading="lazy"
                @error="markBroken(sub.subCategoryImage)"
              />
            </span>
            <span v-if="thumbsOf(category).rest" class="thumb more">
              +{{ thumbsOf(category).rest }}
            </span>
          </div>
          <a
            v-if="missingImages(category).subcategories"
            class="missing-note"
            @click="openCategory(category, 'subcategories')"
          >
            {{ missingImages(category).subcategories }}
            {{
              missingImages(category).subcategories == 1
                ? "subcategory"
                : "subcategories"
            }}
            without image
          </a>
        </div>
      </div>
      <div class="card add" @click="addNewCategory()">
        <span class="plus">+</span>
        New category
      </div>
    </div>
    <button
      class="btn-dark-button mt-20 mx-auto"
      @click="showBulkSelector = true"
    >
      Assign products in bulk
    </button>
    <BulkCategorySelector
      v-if="showBulkSelector"
      @closed="showBulkSelector = false"
    />
  </div>
</template>
<script>
import {
  getCategories,
  getSubcategories,
  requestDeleteCategory,
  hasImage
} from "@/api/store/CategoriesService.js";
import BulkCategorySelector from "@/components/BulkCategorySelector.vue";
import EditCategoryModal from "@/components/shared/EditCategoryModal.vue";
export default {
  components: {
    EditCategoryModal,
    BulkCategorySelector
  },
  data() {
    return {
      categories: [],
      // categoryId -> subcategorías, para miniaturas y avisos de imágenes faltantes
      subcategoriesByCategory: {},
      // URLs que el navegador no pudo cargar (404, dominio viejo…)
      brokenImages: {},
      imagesChecked: false,
      onlyMissing: false,
      editCategory: null,
      editSection: "details",
      showBulkSelector: false
    };
  },
  computed: {
    categoriesShown() {
      if (!this.onlyMissing) return this.categories;
      return this.categories.filter(c => this.missingImages(c).total);
    },
    missingTotal() {
      return this.categories.reduce(
        (acc, c) => {
          const missing = this.missingImages(c);
          acc.categories += missing.category ? 1 : 0;
          acc.subcategories += missing.subcategories;
          return acc;
        },
        { categories: 0, subcategories: 0 }
      );
    }
  },
  mounted() {
    this.loadCategories();
  },
  methods: {
    async loadCategories() {
      this.categories =
        (await getCategories().catch(() => {
          this.error = "Error Loading categories.";
        })) || [];
      await Promise.all(this.categories.map(c => this.loadSubcategories(c.id)));
      this.imagesChecked = true;
    },
    async loadSubcategories(categoryId) {
      const subcategories = await getSubcategories(categoryId).catch(() => []);
      this.$set(this.subcategoriesByCategory, categoryId, subcategories || []);
    },
    hasImage,
    showImage(url) {
      return hasImage(url) && !this.brokenImages[url];
    },
    markBroken(url) {
      this.$set(this.brokenImages, url, true);
    },
    // Miniaturas de subcategorías: las que no tienen imagen primero.
    thumbsOf(category) {
      const max = 8;
      const subs = [...(this.subcategoriesByCategory[category.id] || [])].sort(
        (a, b) =>
          this.showImage(a.subCategoryImage) -
          this.showImage(b.subCategoryImage)
      );
      const rest = subs.length > max ? subs.length - (max - 1) : 0;
      return { shown: rest ? subs.slice(0, max - 1) : subs, rest };
    },
    // Sólo cuenta lo activo: lo inactivo no se ve en la tienda.
    missingImages(category) {
      if (category.status != "1") {
        return { category: false, subcategories: 0, total: 0 };
      }
      const missingCategory = !this.showImage(category.image);
      const missingSubcategories = (
        this.subcategoriesByCategory[category.id] || []
      ).filter(s => s.status == "1" && !this.showImage(s.subCategoryImage))
        .length;
      return {
        category: missingCategory,
        subcategories: missingSubcategories,
        total: (missingCategory ? 1 : 0) + missingSubcategories
      };
    },
    openCategory(category, section = "details") {
      this.editSection = section;
      this.editCategory = category;
    },
    closedCategoryEdition() {
      const category = this.editCategory;
      this.editCategory = null;
      if (category && category.id) this.loadSubcategories(category.id);
    },
    createdCategory(category) {
      this.editCategory = category;
      this.categories.unshift(category);
      this.$set(this.subcategoriesByCategory, category.id, []);
    },
    updatedCategory(category) {
      let cat = this.categories.findIndex(x => {
        return x.id == category.id;
      });
      this.categories.splice(cat, 1, category);
      this.editCategory = this.categories[cat];
    },
    async removeCategory(category) {
      if (
        confirm("Do you want to delete the category " + category.categoryName)
      ) {
        const res = await requestDeleteCategory(category.id);
        console.log("res", res);
        if (res.status == "success") {
          this.categories = this.categories.filter(x => {
            return x.id != category.id;
          });
        } else {
          this.$toast.open({
            message: `${res.message}`,
            type: "error",
            position: "top-right"
          });
        }
      }
    },
    addNewCategory() {
      this.editSection = "details";
      this.editCategory = {
        categoryName: "",
        categoryUrl: "",
        id: null,
        image: null,
        status: 0
      };
    }
  }
};
</script>
<style lang="scss">
.category-edit-modal {
  &.modal {
    .modal-content {
      height: auto;
      width: 80%;
      padding: 0px;
      @media screen and (max-width: 600px) {
        width: 100%;
      }
    }
  }
}
</style>
<style lang="scss" scoped>
@import "@/assets/style/css/App.scss";
.missing-images-alert {
  margin: 10px auto 0;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  align-items: center;
  justify-content: center;
  &.warning {
    background-color: #fff4e5;
    border: 1px solid #f5a623;
    color: #7a4b00;
  }
  &.ok {
    background-color: #e8f6ec;
    border: 1px solid #5cb85c;
    color: #2d6a2d;
  }
  .only-missing {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
  }
}
.list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 1400px;
  margin: 20px auto 0;
  padding: 0 16px;
  .card {
    position: relative;
    display: flex;
    flex-direction: column;
    background-color: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    transition: box-shadow 0.15s ease, transform 0.15s ease;
    &:hover {
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
      .options {
        opacity: 1;
      }
    }
    &.has-missing {
      border-color: #f5a623;
    }
    &.inactive .cover > img {
      filter: grayscale(1);
      opacity: 0.6;
    }
    .cover {
      position: relative;
      aspect-ratio: 185 / 120;
      background-color: #f3f4f6;
      cursor: pointer;
      > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .placeholder {
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 6px;
        border-bottom: 2px dashed #f5a623;
        background-color: #fff8ee;
        color: #a15c00;
        font-size: 13px;
        font-weight: 600;
        img {
          width: 36px;
          opacity: 0.7;
        }
      }
      .status-pill {
        position: absolute;
        top: 8px;
        left: 8px;
        font-size: 11px;
        font-weight: 600;
        padding: 3px 8px;
        border-radius: 10px;
        &.active {
          background-color: rgba(232, 246, 236, 0.95);
          color: #2d6a2d;
        }
        &.disabled {
          background-color: rgba(253, 226, 225, 0.95);
          color: #a61b1b;
        }
      }
      .options {
        position: absolute;
        top: 8px;
        right: 8px;
        display: flex;
        gap: 6px;
        opacity: 0;
        transition: opacity 0.15s ease;
        .icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          padding: 6px;
          border-radius: 8px;
          background-color: rgba(255, 255, 255, 0.95);
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
          &:hover {
            background-color: $M8;
          }
          img {
            width: 100%;
          }
        }
      }
    }
    .body {
      padding: 10px 12px 12px;
      display: flex;
      flex-direction: column;
      gap: 6px;
      .name {
        font-size: 15px;
        font-weight: 600;
        line-height: 1.2;
      }
      .meta {
        font-size: 12px;
        color: #6b7280;
      }
      .thumbs {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        cursor: pointer;
        .thumb {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          overflow: hidden;
          background-color: #f3f4f6;
          border: 1px solid #e5e7eb;
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          &.missing {
            border: 1px dashed #e53e3e;
            background-color: #fde2e1;
          }
          &.more {
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            color: #6b7280;
          }
        }
      }
      .missing-note {
        font-size: 12px;
        font-weight: 600;
        color: #a15c00;
        cursor: pointer;
        &:hover {
          text-decoration: underline;
        }
      }
    }
    &.add {
      min-height: 200px;
      align-items: center;
      justify-content: center;
      gap: 4px;
      border: 2px dashed #d1d5db;
      background-color: #fafafa;
      color: #6b7280;
      font-size: 14px;
      cursor: pointer;
      .plus {
        font-size: 34px;
        line-height: 1;
      }
      &:hover {
        border-color: #9ca3af;
        color: #374151;
      }
    }
  }
}
</style>
